import { alpha } from "@mui/material/styles";
import { getMFPrimaryButtonSx } from "../../utils/dialogStyles";
import { shapeAuthCard, shapeField, heightField } from "../../theme/shape";

const BRAND_FALLBACK = "#006766";

function brandPrimary(theme) {
  return (
    theme?.palette?.customPrimary?.main ||
    theme?.palette?.primary?.main ||
    BRAND_FALLBACK
  );
}

/**
 * Full-viewport background with full-bleed image.
 *
 * Mobile: card anchored near the top so it naturally clears the software
 * keyboard. No JS, no animation, no screen movement — Apple's own approach.
 * paddingTop clears the status bar / Dynamic Island via safe-area-inset-top
 * and adds a small breathing gap above the card.
 *
 * Tablet / desktop: centred as before.
 */
export function getAuthPageOuterSx() {
  return {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    justifyContent: { xs: "flex-start", sm: "center" },
    boxSizing: "border-box",
    paddingTop: {
      xs: "max(env(safe-area-inset-top, 0px) + 16px, 32px)",
      sm: "max(12px, env(safe-area-inset-top, 0px))",
    },
    paddingBottom: {
      xs: "max(env(safe-area-inset-bottom, 0px), 24px)",
      sm: "max(12px, env(safe-area-inset-bottom, 0px))",
    },
    backgroundColor: "#f5f5f5",
    backgroundImage: "url('/auth-bg.jpg')",
    backgroundSize: "cover",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundAttachment: "scroll",
  };
}

/**
 * One frosted "liquid glass" surface. Single backdrop-filter for the card
 * only (fields stay opaque-ish to avoid per-input GPU cost).
 */
export function getAuthGlassCardSx(theme) {
  const p = brandPrimary(theme);
  return {
    borderRadius: shapeAuthCard,
    px: { xs: 2.25, sm: 3 },
    pt: { xs: 2.25, sm: 3 },
    pb: { xs: 2, sm: 2.75 },
    border: `1px solid ${alpha("#ffffff", 0.70)}`,
    backgroundColor: alpha("#ffffff", 0.90),
    backdropFilter: "saturate(180%) blur(28px)",
    WebkitBackdropFilter: "saturate(180%) blur(28px)",
    boxShadow: [
      `0 0 0 1px ${alpha(p, 0.07)}`,
      `0 20px 60px ${alpha("#000000", 0.12)}`,
      `inset 0 1px 0 ${alpha("#ffffff", 0.90)}`,
    ].join(", "),
    "@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))":
      {
        backgroundColor: "rgba(255,255,255,0.98)",
        backdropFilter: "none",
        WebkitBackdropFilter: "none",
      },
  };
}

/**
 * Input fields inspired by the glossary Search field:
 * near-invisible tint at rest, soft border on hover, glow ring on focus.
 * Lights up to pure white on focus — inviting, not clinical.
 */
export function getAuthGlassFieldSx(theme) {
  const p = brandPrimary(theme);
  const err = theme.palette.error.main;
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: shapeField,
      backgroundColor: "rgba(0,0,0,0.04)",
      fontSize: "16px",
      minHeight: heightField,
      transition: "background-color 0.15s ease, box-shadow 0.15s ease",
      "& fieldset": {
        borderColor: "transparent",
        transition: "border-color 0.15s ease",
      },
      "&:hover fieldset": { borderColor: "rgba(0,0,0,0.14)" },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: `0 0 0 3px ${alpha(p, 0.14)}`,
      },
      "&.Mui-focused fieldset": {
        borderColor: alpha(p, 0.50),
        borderWidth: "1.5px",
      },
      "&.Mui-error fieldset": { borderColor: alpha(err, 0.55) },
      "&.Mui-error": {
        backgroundColor: alpha(err, 0.03),
        boxShadow: `0 0 0 3px ${alpha(err, 0.10)}`,
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      color: "rgba(0,0,0,0.45)",
      "&.Mui-focused": { color: p },
      "&.Mui-error": { color: err },
    },
    "& .MuiOutlinedInput-input": {
      py: "12px",
    },
  };
}

/**
 * Auth-page CTA — delegates to the shared getMFPrimaryButtonSx so all
 * dialogs render the same button. Kept as a named export so existing
 * callers (LoginPage, SignupPage, ForgetPswPage, AccountProfileForm, etc.)
 * continue to work without changes.
 */
export function getAuthPrimaryButtonSx(theme) {
  return getMFPrimaryButtonSx(theme);
}
